import { MAINTENANCE_MESSAGE_EN } from "../i18n/maintenanceMessages";

function tryParseJson(value) {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return undefined;
  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

function unwrapMessage(value) {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const parsed = tryParseJson(trimmed);
    return parsed ? unwrapMessage(parsed) : trimmed;
  }
  if (typeof value === "object") {
    return (
      unwrapMessage(value.detail?.message) ||
      unwrapMessage(value.detail) ||
      unwrapMessage(value.message) ||
      unwrapMessage(value.error_message) ||
      unwrapMessage(value.errorMessage) ||
      unwrapMessage(value.error)
    );
  }
  return "";
}

function extractCode(value) {
  if (value == null) return "";
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    return parsed ? extractCode(parsed) : "";
  }
  if (typeof value !== "object") return "";
  return (
    value.code ||
    value.error_code ||
    value.errorCode ||
    value.detail?.code ||
    value.detail?.error_code ||
    value.detail?.errorCode ||
    extractCode(value.detail)
  );
}

function normalizeCode(code) {
  return String(code || "").trim().toLowerCase();
}

function dailyLimitMessage(quota) {
  if (quota === "feasibility") return "You have reached today's demo feasibility run limit.";
  return "You have reached today's demo run limit.";
}

export function formatApiError(error, fallback = "Failed to complete request.", options = {}) {
  if (!error) return fallback;

  const rawMessage = String(error?.message ?? error ?? "").trim();
  const parsedFromMessage = tryParseJson(rawMessage);
  const source = parsedFromMessage || (typeof error === "object" ? error : undefined);
  const code = normalizeCode(extractCode(source) || extractCode(error));
  const status = Number(error?.status || source?.status || source?.status_code || source?.statusCode || 0);
  if (code === "maintenance_mode") return MAINTENANCE_MESSAGE_EN;

  if (code === "demo_expired") return "Your demo period has expired.";
  if (code === "demo_locked" || code === "demo_misconfigured") return "Demo account is locked.";
  if (code === "demo_project_limit_reached") return "Demo project limit reached.";
  if (
    code === "daily_trigger_limit_reached" ||
    code === "daily_run_limit_reached" ||
    code === "demo_daily_limit_reached" ||
    code === "demo_market_optimization_limit_reached"
  ) {
    return dailyLimitMessage(options.quota);
  }
  if (
    code === "daily_feasibility_limit_reached" ||
    code === "feasibility_limit_reached" ||
    code === "demo_feasibility_limit_reached"
  ) {
    return "You have reached today's demo feasibility run limit.";
  }
  if (
    code === "concurrent_job_limit_reached" ||
    code === "concurrent_limit_reached" ||
    code === "job_already_running"
  ) {
    return "Another job is already running. Please wait for it to finish before starting a new one.";
  }
  if (
    code === "max_simulation_range_exceeded" ||
    code === "simulation_range_exceeded" ||
    code === "max_range_exceeded"
  ) {
    return "The selected simulation range exceeds the maximum allowed range.";
  }
  if (code === "admin_only" || code === "admin_required") {
    return "This action is admin-only.";
  }
  if (code === "unauthorized" || code === "not_authenticated") {
    return "You are not authorized to perform this action.";
  }
  if (code === "rate_limit_reached" || code === "too_many_requests") {
    return "Rate limit reached. Please wait and try again.";
  }

  const message = unwrapMessage(source) || unwrapMessage(error) || rawMessage;
  const normalizedMessage = String(message || "").trim();
  const haystack = `${code} ${normalizedMessage}`.toLowerCase();

  // `fetch` rejects with a bare TypeError when the request never reached the
  // server, or when the reply carried no CORS headers -- which is also what a
  // crashed request looks like from the browser. The browser's own wording
  // ("Failed to fetch", "NetworkError...", "Load failed" on Safari) is an
  // internal detail; the reader only needs to know it was not their doing.
  if (
    !status &&
    /^(typeerror:\s*)?(failed to fetch|networkerror when attempting to fetch resource\.?|load failed|network request failed)$/i.test(
      normalizedMessage
    )
  ) {
    return "Unexpected error. The server could not be reached — please try again.";
  }

  if (status === 401) return "You are not authorized to perform this action.";
  if (status === 403) {
    if (/admin[-_ ]?only|admin required/.test(haystack)) return "This action is admin-only.";
    return "You are not authorized to perform this action.";
  }
  if (status === 429 || /rate limit|too many requests/.test(haystack)) {
    return "Rate limit reached. Please wait and try again.";
  }
  if (/concurrent|already running|another job/.test(haystack)) {
    return "Another job is already running. Please wait for it to finish before starting a new one.";
  }
  if (/max(imum)? .*simulation .*range|simulation .*range .*exceed|range .*exceed/.test(haystack)) {
    return "The selected simulation range exceeds the maximum allowed range.";
  }
  if (/daily .*limit|limit .*today/.test(haystack)) return dailyLimitMessage(options.quota);
  if (/demo .*project .*limit|project .*limit .*reached|max(imum)? .*projects/.test(haystack)) {
    return "Demo project limit reached.";
  }
  if (/demo .*expired|expired .*demo/.test(haystack)) return "Your demo period has expired.";
  if (/demo .*locked|account .*locked/.test(haystack)) return "Demo account is locked.";
  if (/admin[-_ ]?only|admin required/.test(haystack)) return "This action is admin-only.";
  if (/unauthorized|not authorized|not authenticated/.test(haystack)) {
    return "You are not authorized to perform this action.";
  }

  return normalizedMessage || fallback;
}

